import {COMPANY_ID} from '@constants/auth.constant'
import {DASHBOARD} from '@constants/internal-route.constant'
import {useAuth} from '@context/Auth'
import {LocalStorageService} from '@services/utils/localStorage.service'
import {ReactNode} from 'react'
import {Navigate} from 'react-router-dom'

// Roles that bypass feature gating and see everything.
const SUPER_ROLES = ['COMPANY_ADMIN']

/**
 * Reads the logged-in user's granted features (from portal verify-token → getUserAppAcl,
 * stored on userInfo.features for app_name=ERPBOI_AMS) and answers menu/route
 * permission questions.
 *
 * Fail-CLOSED once the ACL is known: while verify-token is still in flight (isChecking —
 * the splash screen is shown) access is allowed so users aren't locked out mid-load, but
 * once the check resolves a missing/empty feed is DENIED. COMPANY_ADMIN bypasses.
 */
export const usePermissions = () => {
  const {userInfo, isChecking} = useAuth()

  // Role is per-company — verify-token has no usable top-level roleKey, so resolve it from the
  // selected company (companies[].roleKey, falling back to roleName which carries the same value).
  const currentCid = LocalStorageService.get(COMPANY_ID)
  const currentCompany =
    userInfo?.companies?.find((c) => c.companyId === currentCid) || userInfo?.companies?.[0]
  const roleKey = userInfo?.roleKey || currentCompany?.roleKey || currentCompany?.roleName
  const isSuperAdmin = !!roleKey && SUPER_ROLES.includes(roleKey)
  const features = userInfo?.features
  const loaded = Array.isArray(features)

  // Normalize a route/feature url for comparison — drop any query string / hash so a menu
  // item whose `to` carries a query (e.g. Reports Manager `?report=COA`) still matches the
  // feature url stored in the ACL.
  const normalize = (u?: string) => (u ? u.split('?')[0].split('#')[0] : '')

  const grantedShow = new Set(
    (features || [])
      .filter((f) => Number(f?.canShow) === 1 && f?.url)
      .map((f) => normalize(f.url as string))
  )

  const hasFeature = (url?: string): boolean => {
    if (isSuperAdmin) return true
    if (isChecking) return true // ACL still being fetched (splash shown) — don't lock out mid-load
    return loaded && !!url && grantedShow.has(normalize(url)) // resolved: default-DENY when absent
  }

  const hasAny = (urls: string[]): boolean => {
    if (isSuperAdmin) return true
    if (isChecking) return true
    return loaded && urls.some((u) => grantedShow.has(normalize(u)))
  }

  /**
   * Action-level ACL. portal has always sent canSave / canUpdate / canDelete alongside
   * canShow, and until now nothing read them — a role granted view-only access to a screen
   * still saw a working Add button, and only found out the answer was no when the server
   * refused. Same fail-open-while-checking, default-DENY-once-resolved rule as hasFeature,
   * and COMPANY_ADMIN bypasses.
   *
   * This is a UI affordance, not a control: the server-side SEC2 list in WebCorsConfig is
   * what actually stops a write.
   */
  const featureFor = (url?: string) =>
    (features || []).find((f) => f?.url && normalize(f.url as string) === normalize(url))

  const can = (url: string | undefined, action: 'save' | 'update' | 'delete'): boolean => {
    if (isSuperAdmin) return true
    if (isChecking) return true
    if (!loaded) return false
    const f = featureFor(url)
    if (!f) return false
    const flag = action === 'save' ? f.canSave : action === 'update' ? f.canUpdate : f.canDelete
    return Number(flag) === 1
  }

  const canSave = (url?: string) => can(url, 'save')
  const canUpdate = (url?: string) => can(url, 'update')
  const canDelete = (url?: string) => can(url, 'delete')

  return {
    isSuperAdmin,
    roleKey,
    features: features || [],
    loaded,
    hasFeature,
    hasAny,
    can,
    canSave,
    canUpdate,
    canDelete,
  }
}

/**
 * Route guard: renders children only if the user can see at least one of `anyOf`
 * feature URLs, otherwise redirects to the dashboard.
 */
export const RequireFeature = ({anyOf, children}: {anyOf: string[]; children: ReactNode}) => {
  const {hasAny} = usePermissions()
  return hasAny(anyOf) ? <>{children}</> : <Navigate to={DASHBOARD} replace />
}
